import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Settings2, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { tanggal } from "@/lib/format";
import { CATEGORIES, CATEGORY_KEYS, STATUS, ROLE_LABELS, catOf } from "@/lib/activity";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ActivityForm } from "./ActivityForm";
import { ActivityManage } from "./ActivityManage";

export const ActivityTable = ({ mode = "upcoming" }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [manage, setManage] = useState(null);
  const [del, setDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (mode !== "all") p.set("upcoming", mode === "upcoming" ? "true" : "false");
      if (category !== "all") p.set("category", category);
      if (status !== "all") p.set("status", status);
      const { data } = await api.get(`/activities?${p}`);
      setItems(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [mode, category, status]);

  useEffect(() => { load(); }, [load]);

  const remove = async (series) => {
    try {
      await api.delete(`/activities/${del.id}${series ? "?series=true" : ""}`);
      toast.success("Kegiatan dihapus");
      setDel(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const ql = q.toLowerCase();
  const filtered = ql ? items.filter((a) => [a.title, a.location, ...Object.values(a.people || {}).map((p) => p.name)].some((s) => String(s || "").toLowerCase().includes(ql))) : items;
  const prefix = `admin-activity-${mode}`;

  return (
    <div className="rounded-3xl bg-white border border-emerald-900/10 overflow-hidden" data-testid={`${prefix}-table-wrap`}>
      <div className="p-4 border-b border-emerald-900/10 flex flex-wrap items-center gap-2">
        <div className="relative"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari..." className="pl-9 rounded-full w-48" data-testid={`${prefix}-search`} /></div>
        <Select value={category} onValueChange={setCategory}><SelectTrigger className="w-56 rounded-full" data-testid={`${prefix}-category-filter`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Kategori</SelectItem>{CATEGORY_KEYS.map((k) => <SelectItem key={k} value={k}>{CATEGORIES[k].label}</SelectItem>)}</SelectContent></Select>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-44 rounded-full" data-testid={`${prefix}-status-filter`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Status</SelectItem>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent></Select>
        <span className="text-sm text-slate-500" data-testid={`${prefix}-count`}>{filtered.length} kegiatan</span>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="ml-auto rounded-full emerald-bg hover:bg-[#145c48]" data-testid="admin-activity-add-btn"><Plus size={16} className="mr-1" /> Tambah Kegiatan</Button>
      </div>
      <div className="overflow-x-auto">
        <Table data-testid={`${prefix}-table`}>
          <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Kegiatan</TableHead><TableHead>Kategori</TableHead><TableHead>Petugas</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400"><Loader2 className="animate-spin inline mr-2" size={16} />Memuat...</TableCell></TableRow>}
            {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400" data-testid={`${prefix}-empty`}>Belum ada kegiatan.</TableCell></TableRow>}
            {filtered.map((a) => {
              const c = catOf(a.category); const st = STATUS[a.status] || STATUS.terjadwal;
              return (
                <TableRow key={a.id} data-testid={`activity-row-${a.id}`}>
                  <TableCell className="whitespace-nowrap">{tanggal(a.date)}{a.time && <span className="text-slate-400"> · {a.time}</span>}</TableCell>
                  <TableCell className="font-medium max-w-xs"><p className="truncate">{a.title}</p><p className="text-xs text-slate-400 truncate">{a.location}</p></TableCell>
                  <TableCell><span className="inline-flex items-center gap-1.5 text-xs font-medium"><span className="h-2.5 w-2.5 rounded-full" style={{ background: c.to }} />{c.short}</span></TableCell>
                  <TableCell className="text-xs text-slate-600 max-w-[200px] truncate">{Object.entries(a.people || {}).map(([r, p]) => `${ROLE_LABELS[r]}: ${p.name}`).join(", ") || a.pic || "-"}</TableCell>
                  <TableCell><Badge className={`${st.tone} hover:${st.tone}`} data-testid={`activity-status-${a.id}`}>{st.label}</Badge></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" title="Kelola" onClick={() => setManage(a)} data-testid={`activity-manage-${a.id}`}><Settings2 size={15} /></Button>
                    <Button variant="ghost" size="icon" title="Edit" onClick={() => { setEditing(a); setFormOpen(true); }} data-testid={`activity-edit-${a.id}`}><Pencil size={15} /></Button>
                    <Button variant="ghost" size="icon" className="text-red-600" title="Hapus" onClick={() => setDel(a)} data-testid={`activity-delete-${a.id}`}><Trash2 size={15} /></Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="activity-form-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Kegiatan" : "Tambah Kegiatan"}</DialogTitle></DialogHeader>
          {formOpen && <ActivityForm initial={editing} onCancel={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); load(); }} />}
        </DialogContent>
      </Dialog>

      <ActivityManage activity={manage} onClose={() => setManage(null)} onChanged={load} />

      <AlertDialog open={!!del} onOpenChange={(v) => !v && setDel(null)}>
        <AlertDialogContent data-testid="activity-delete-dialog">
          <AlertDialogHeader><AlertDialogTitle>Hapus kegiatan "{del?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>{del?.series_id ? "Kegiatan ini bagian dari jadwal berulang. Anda dapat menghapus hanya jadwal ini atau semua jadwal berikutnya dalam seri." : "Data kegiatan dan daftar hadirnya akan dihapus permanen."}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="activity-delete-cancel">Batal</AlertDialogCancel>
            {del?.series_id && <AlertDialogAction onClick={() => remove(true)} className="bg-red-800 hover:bg-red-900" data-testid="activity-delete-series">Hapus Seri Berikutnya</AlertDialogAction>}
            <AlertDialogAction onClick={() => remove(false)} className="bg-red-600 hover:bg-red-700" data-testid="activity-delete-confirm">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
